import { useMemo } from 'react'

import {
  Combobox,
  ComboboxCollection,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/frontend/components/ui/combobox'
import { cn } from '@/frontend/lib/utils'

type SnapInterval = 5 | 10 | 15 | 20 | 30 | 60

interface TimePickerProps {
  value?: string
  onChange?: (time: string) => void
  snap?: SnapInterval
  min?: string
  max?: string
  placeholder?: string
  disabled?: boolean
  className?: string
}

/** Parse "HH:mm" to total minutes from midnight */
function parseTime(time: string): number {
  const [h, m] = time.split(':').map(Number)
  return h * 60 + m
}

/** Format total minutes from midnight to "HH:mm" */
function formatTime(totalMinutes: number): string {
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** Generate the list of time slot strings */
function generateSlots(snap: number, min: string, max: string): string[] {
  const start = parseTime(min)
  const end = parseTime(max)
  const slots: string[] = []
  for (let m = start; m <= end; m += snap) {
    slots.push(formatTime(m))
  }
  return slots
}

function TimePicker({
  value,
  onChange,
  snap = 15,
  min = '00:00',
  max = '23:59',
  placeholder = 'Pick time',
  disabled,
  className,
}: TimePickerProps) {
  const slots = useMemo(() => generateSlots(snap, min, max), [snap, min, max])

  return (
    <Combobox<string>
      value={value ?? null}
      onValueChange={(slot) => {
        if (slot) onChange?.(slot)
      }}
      isItemEqualToValue={(a, b) => a === b}
      items={slots}
      disabled={disabled}
    >
      <ComboboxInput
        placeholder={placeholder}
        className={cn('w-full cursor-default [&_input]:cursor-default hover:bg-muted', className)}
      />
      <ComboboxContent className="w-40">
        <ComboboxList>
          <ComboboxCollection>
            {(slot) => (
              <ComboboxItem key={slot} value={slot}>
                {slot}
              </ComboboxItem>
            )}
          </ComboboxCollection>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}

export { type SnapInterval, TimePicker, type TimePickerProps }
